/**
 * Cómo se dice en español lo que contesta el motor de aplicaciones.
 *
 * **Una sola copia, consumida por las dos pantallas que previsualizan**: el diálogo de Exigibilidad
 * —un pago con mucha ceremonia— y el paso de impacto del asistente de carga —cuarenta compactos—.
 * Lo que comparten no es la pantalla; es el diccionario.
 *
 * Vive en la raíz de `private/`, con el precedente de `_cobranza-ui.scss`, por la misma razón: lo que
 * cruza dos módulos no puede vivir dentro de uno de ellos.
 *
 * ## Por qué existe este archivo
 *
 * Los dos mapas estaban copiados, **y ya habían divergido**: a Exigibilidad le faltaba
 * `LEASE_WILL_SETTLE` —la bloqueante que introdujo la D1—, así que justo en el momento de decidir si
 * se liquida un arrendamiento, la pantalla enseñaba el código en crudo. Y las explicaciones de error
 * existían **solo en un lado**: el mismo fallo del motor se explicaba bien en una pantalla y salía
 * como el mensaje pelado del servidor en la otra.
 *
 * **Al agregar un código nuevo al motor, se agrega aquí y las dos pantallas lo heredan.** El editor de
 * saldos también lo lee: `LEASE_WILL_SETTLE` y `LEASE_WILL_REACTIVATE` los devuelven igual la aplicación
 * de pagos y el editor, y un mismo cambio de estado no puede decirse de dos formas.
 */

/**
 * Las advertencias del motor, por código.
 *
 * Las **bloqueantes** (`blocking: true`) llegan con una casilla que hay que marcar antes de aplicar;
 * las demás son informativas. Esa distinción la manda el motor en cada aviso, no este mapa.
 */
export const APPLICATION_WARNING_LABELS: Record<string, string> = {
  PARTIAL_PERIOD: 'La letra queda parcial',
  LEFTOVER: 'Queda un sobrante disponible',
  LATE_REGISTRATION: 'Registro tardío (nuestro)',
  PAID_WITH_BALANCE: 'Letra marcada pagada con saldo',
  OUT_OF_ORDER_PAYMENT: 'Pago fuera de orden (informativa)',
  INVOICED_PERIODS: 'Letras con factura',
  REP_INVALIDATION: 'Se invalidará un complemento (REP)',
  LEASE_LIQUIDATED: 'El arrendamiento está liquidado',
  LEASE_WILL_SETTLE: 'El arrendamiento queda liquidado',
  LEASE_WILL_REACTIVATE: 'El arrendamiento vuelve a Activo',
  //* La operación cae sobre un arrendamiento que ya no está Activo (p. ej. deshacer una condonación en uno liquidado).
  LEASE_NOT_ACTIVE: 'El arrendamiento no está activo',
  //* Al cancelar: la letra tenía menos aplicado del que la aplicación dice, o su desglose por concepto no
  //* alcanzó. Informativas: la cancelación sigue, pero el motivo tiene que quedar a la vista.
  APPLIED_SHORTFALL: 'La letra tenía menos aplicado de lo que la aplicación dice',
  BREAKDOWN_SHORTFALL: 'El desglose por concepto de la letra no alcanzó',
}

/** Cómo queda la letra tras aplicar. El motor devuelve el estado del periodo en crudo. */
export const PERIOD_RESULT_LABELS: Record<string, string> = {
  PAID: 'Pagada',
  PARTIAL: 'Parcial',
  PENDING: 'Pendiente',
  OVERDUE: 'Atrasada',
  ANTICIPATED: 'Anticipada',
  MORA_PENDING: 'Mora pendiente',
  UPCOMING: 'Por vencer',
}

/** Los conceptos en los que el motor reparte el dinero de una letra. */
export const CONCEPT_LABELS: Record<string, string> = {
  interest: 'Interés',
  moratoriums: 'Mora',
  accessories: 'Accesorios',
  capital: 'Capital',
  inputIva: 'IVA',
}

/** Un fallo del motor, explicado: qué pasó y cómo sigo. */
export interface ApplicationErrorExplanation {
  titulo: string
  que: string
  comoSigo: string
}

/**
 * Los fallos conocidos del motor, en orden de comprobación.
 *
 * Se emparejan por **patrón sobre el mensaje del servidor** y no por código, porque el motor no manda
 * códigos de error: manda una frase. Es frágil a propósito y está asumido — el fallback enseña el
 * mensaje crudo, que es exactamente lo que se veía antes en todos los casos.
 */
export const APPLICATION_ERROR_EXPLANATIONS: (ApplicationErrorExplanation & { patron: RegExp })[] = [
  {
    patron: /inconsistencia en montos a revertir/i,
    titulo: 'Esta aplicación no se puede cancelar sola',
    que: 'El reparto por concepto que guarda la aplicación no suma lo mismo que el monto que dice haber aplicado. Revertirla dejaría los saldos de la letra mal.',
    comoSigo: 'Es un desfase de datos, no algo que hayas hecho mal. Corrige la letra con «Editar saldos» o repórtalo con la clave del pago.',
  },
  {
    patron: /ya ha sido cancelada/i,
    titulo: 'Esta aplicación ya estaba cancelada',
    que: 'Alguien más la canceló mientras tenías el diálogo abierto.',
    comoSigo: 'Cierra y pulsa «Actualizar» para ver el libro como está ahora.',
  },
  {
    patron: /ya ha sido aplicad|no hay monto pendiente por aplicar/i,
    titulo: 'Este pago ya no tiene saldo por aplicar',
    que: 'Todo su dinero está repartido entre letras, aquí o en otro arrendamiento del cliente.',
    comoSigo: 'Cierra y pulsa «Actualizar». Si esperabas que quedara saldo, ábrelo en el cajón y revisa a dónde fue.',
  },
  {
    patron: /estado (partial|pending) debe tener|pendingtoapply no puede/i,
    titulo: 'Los saldos de este pago no cuadran',
    que: 'Lo aplicado y lo disponible del pago no suman su monto total, y el motor no reparte sobre un pago así.',
    comoSigo: 'Ábrelo en el cajón y revisa a dónde fue su dinero; si el saldo no cuadra, repórtalo con la clave.',
  },
  {
    patron: /excede el monto disponible|no hay suficiente monto en el pago/i,
    titulo: 'El monto no cabe en el pago',
    que: 'Lo que pediste aplicar es más de lo que al pago le queda disponible.',
    comoSigo: 'Baja el monto a aplicar al disponible que muestra el paso 1.',
  },
  {
    patron: /tabla de amortización|no tiene datos de términos|no se encontró la cotización/i,
    titulo: 'El arrendamiento no tiene tabla de amortización',
    que: 'Sin tabla no hay letras a las que aplicar el dinero.',
    comoSigo: 'Selecciona en Cotizaciones el término aprobado por el cliente y vuelve a intentar.',
  },
  {
    //! El 409 del apretón de manos (F12). En el asistente es el caso normal, no la excepción: entre
    //! previsualizar cuarenta pagos y aplicarlos pasan minutos, y dos pagos del mismo arrendamiento
    //! se invalidan entre sí. Se explica como lo que es —hay que recalcular—, no como un error.
    patron: /cambió desde la previsualización|statehash|estado del arrendamiento cambió/i,
    titulo: 'El arrendamiento cambió desde el cálculo',
    que: 'Alguien aplicó o ajustó algo en este arrendamiento después de que se calculó el impacto, así que el plan que tienes delante ya no describe lo que pasaría.',
    comoSigo: 'Vuelve a calcular el impacto. Nada se escribió.',
  },
  {
    patron: /internal server error|error interno/i,
    titulo: 'El servidor no pudo terminar el cálculo',
    que: 'La petición falló del lado del servidor. No es por lo que capturaste, y nada se escribió.',
    comoSigo: 'Vuelve a previsualizar. Si se repite, repórtalo con la clave del pago y la hora.',
  },
]

/**
 * Explica un fallo del motor. `null` cuando no hay mensaje: no hay nada que explicar.
 *
 * Cuando el fallo **no** está en el diccionario se conserva el comportamiento de siempre: el mensaje
 * del servidor ocupa el cuerpo, sin repetirlo abajo como «detalle técnico». Un mensaje que aparece
 * dos veces en la misma caja hace dudar de si son dos cosas distintas.
 */
export function explainApplicationError(mensaje: string): (ApplicationErrorExplanation & { detalle: string }) | null {
  if (!mensaje) return null

  const conocido = APPLICATION_ERROR_EXPLANATIONS.find((e) => e.patron.test(mensaje))
  if (conocido) return { titulo: conocido.titulo, que: conocido.que, comoSigo: conocido.comoSigo, detalle: mensaje }

  return {
    titulo: 'No se pudo calcular el impacto',
    que: mensaje,
    comoSigo: 'Nada se escribió. Vuelve a previsualizar; si se repite, repórtalo con la clave del pago.',
    detalle: '',
  }
}
